import { useState, useCallback } from 'react'
import {
  Brain, RefreshCw, Sparkles, MessageCircle, Search, Send,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

export default function AIInsightsPage() {
  const [summary, setSummary] = useState<any>(null)
  const [question, setQuestion] = useState('')
  const [answer, setAnswer] = useState<any>(null)
  const [analyseTopic, setAnalyseTopic] = useState('')
  const [analysis, setAnalysis] = useState<any>(null)
  const [curated, setCurated] = useState<any>(null)
  const [providers, setProviders] = useState<any>(null)
  const [tab, setTab] = useState<'summary' | 'curated' | 'ask' | 'analyse' | 'settings'>('summary')
  const [loading, setLoading] = useState(false)

  const generateSummary = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.post('/api/ai/summarise')
      setSummary(data)
    } finally { setLoading(false) }
  }, [])

  const askQuestion = async () => {
    if (!question.trim()) return
    setLoading(true)
    try {
      const { data } = await api.post('/api/ai/ask', { question: question.trim() })
      setAnswer(data)
    } finally { setLoading(false) }
  }

  const runAnalysis = async () => {
    if (!analyseTopic.trim()) return
    setLoading(true)
    try {
      const { data } = await api.post('/api/ai/analyse', { topic: analyseTopic.trim() })
      setAnalysis(data)
    } finally { setLoading(false) }
  }

  return (
    <div className="space-y-4 animate-fade-in max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Brain size={20} className="text-purple" /> AI Intelligence
          </h1>
          <p className="text-[10px] text-text-secondary">AI-powered analysis of 1600+ articles from 230+ feeds</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {([
          { key: 'summary' as const, label: 'Daily Summary', icon: Sparkles },
          { key: 'curated' as const, label: 'Curated for You', icon: Sparkles },
          { key: 'ask' as const, label: 'Ask a Question', icon: MessageCircle },
          { key: 'analyse' as const, label: 'Deep Analysis', icon: Search },
          { key: 'settings' as const, label: 'AI Provider', icon: Brain },
        ]).map(t => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)}
            className={clsx('badge text-[10px] cursor-pointer px-3 py-1.5',
              tab === t.key ? 'badge-purple' : 'bg-surface-card text-text-muted border border-border')}>
            <t.icon size={11} /> {t.label}
          </button>
        ))}
      </div>

      {/* Daily Summary */}
      {tab === 'summary' && (
        <div className="space-y-3">
          {!summary && (
            <div className="card p-6 text-center">
              <Brain size={40} className="mx-auto mb-3 text-purple opacity-50" />
              <p className="text-sm text-text-primary mb-2">Generate AI Executive Summary</p>
              <p className="text-[10px] text-text-muted mb-4">Analyses today's top stories and produces an actionable CTO briefing</p>
              <button type="button" onClick={generateSummary} disabled={loading} className="btn-primary text-xs">
                {loading ? <><RefreshCw size={12} className="animate-spin" /> Generating...</> : <><Sparkles size={12} /> Generate Summary</>}
              </button>
            </div>
          )}
          {summary && (
            <div className="card p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
                  <Sparkles size={14} className="text-purple" /> AI Executive Summary
                </h2>
                <div className="flex items-center gap-2">
                  <span className="text-[9px] text-text-muted">{summary.articles_analysed} articles analysed</span>
                  <button type="button" onClick={generateSummary} disabled={loading} className="btn-secondary text-[10px] py-1 px-2">
                    {loading ? <RefreshCw size={10} className="animate-spin" /> : <RefreshCw size={10} />} Regenerate
                  </button>
                </div>
              </div>
              <div className="prose prose-sm prose-invert max-w-none text-xs text-text-secondary leading-relaxed whitespace-pre-wrap">
                {summary.summary.split('\n').map((line: string, i: number) => {
                  if (line.startsWith('**') && line.endsWith('**')) return <h3 key={i} className="text-sm font-bold text-text-primary mt-3 mb-1">{line.replace(/\*\*/g, '')}</h3>
                  if (line.startsWith('## ')) return <h3 key={i} className="text-sm font-bold text-text-primary mt-3 mb-1">{line.slice(3)}</h3>
                  if (line.startsWith('- ') || line.startsWith('* ')) return <li key={i} className="ml-4 text-text-secondary">{line.slice(2)}</li>
                  if (line.match(/^\d+\./)) return <li key={i} className="ml-4 text-text-secondary">{line}</li>
                  return <p key={i} className="mb-1">{line}</p>
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Ask a Question */}
      {tab === 'ask' && (
        <div className="space-y-3">
          <div className="card p-4">
            <h2 className="text-xs font-semibold text-text-primary mb-3">Ask about your tech intelligence data</h2>
            <div className="flex gap-2">
              <input value={question} onChange={e => setQuestion(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') askQuestion() }}
                placeholder="e.g. What are the key AI trends this week? / Is NVIDIA still dominant in GPU? / What cyber threats should I worry about?"
                className="input py-2 text-xs flex-1" />
              <button type="button" onClick={askQuestion} disabled={loading || !question.trim()} className="btn-primary text-xs">
                {loading ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />} Ask
              </button>
            </div>
            <div className="flex gap-1 mt-2 flex-wrap">
              {['What are the top AI developments this week?', 'Which cloud providers had issues?', 'What semiconductor trends matter?', 'Summarise the cyber threat landscape'].map(q => (
                <button key={q} type="button" onClick={() => { setQuestion(q); }} className="text-[9px] text-text-muted bg-surface-hover px-2 py-0.5 rounded hover:text-text-secondary">{q}</button>
              ))}
            </div>
          </div>
          {answer && (
            <div className="card p-4">
              <p className="text-[10px] text-text-muted mb-2">Q: {answer.question} ({answer.articles_searched} articles searched)</p>
              <div className="text-xs text-text-secondary leading-relaxed whitespace-pre-wrap">{answer.answer}</div>
            </div>
          )}
        </div>
      )}

      {/* Deep Analysis */}
      {tab === 'analyse' && (
        <div className="space-y-3">
          <div className="card p-4">
            <h2 className="text-xs font-semibold text-text-primary mb-3">Deep analysis of a topic, company, or technology</h2>
            <div className="flex gap-2">
              <input value={analyseTopic} onChange={e => setAnalyseTopic(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') runAnalysis() }}
                placeholder="e.g. NVIDIA, Kubernetes, Quantum Computing, AI Regulation..."
                className="input py-2 text-xs flex-1" />
              <button type="button" onClick={runAnalysis} disabled={loading || !analyseTopic.trim()} className="btn-primary text-xs">
                {loading ? <RefreshCw size={12} className="animate-spin" /> : <Search size={12} />} Analyse
              </button>
            </div>
            <div className="flex gap-1 mt-2 flex-wrap">
              {['NVIDIA', 'Kubernetes', 'AI Regulation', 'Quantum Computing', 'Cybersecurity Threats', 'Cloud Pricing'].map(t => (
                <button key={t} type="button" onClick={() => { setAnalyseTopic(t); }} className="text-[9px] text-text-muted bg-surface-hover px-2 py-0.5 rounded hover:text-text-secondary">{t}</button>
              ))}
            </div>
          </div>
          {analysis && (
            <div className="card p-4">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-sm font-semibold text-text-primary">{analysis.topic}</h3>
                <span className="badge-blue text-[9px]">{analysis.articles_found} articles from {analysis.sources?.length || 0} sources</span>
              </div>
              <div className="text-xs text-text-secondary leading-relaxed whitespace-pre-wrap">{analysis.analysis}</div>
              {analysis.sources?.length > 0 && (
                <div className="mt-3 flex gap-1 flex-wrap">
                  {analysis.sources.map((s: string) => (
                    <span key={s} className="text-[8px] bg-surface-hover px-1.5 py-0.5 rounded text-text-muted">{s}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {/* Curated */}
      {tab === 'curated' && (
        <div className="space-y-3">
          {!curated && (
            <div className="card p-6 text-center">
              <Sparkles size={40} className="mx-auto mb-3 text-amber opacity-50" />
              <p className="text-sm text-text-primary mb-2">AI-Curated Top Stories</p>
              <p className="text-[10px] text-text-muted mb-4">AI reviews today's articles and picks the 10 most important for a CTO</p>
              <button type="button" onClick={async () => { setLoading(true); try { const { data } = await api.post('/api/ai/curate'); setCurated(data) } finally { setLoading(false) } }} disabled={loading} className="btn-primary text-xs">
                {loading ? <><RefreshCw size={12} className="animate-spin" /> Curating...</> : <><Sparkles size={12} /> Curate Top Stories</>}
              </button>
            </div>
          )}
          {curated?.curated?.map((a: any, i: number) => (
            <div key={i} className="card p-3 border border-purple/10">
              <div className="flex items-start gap-2">
                <span className="text-lg font-bold text-purple shrink-0 w-6">#{i + 1}</span>
                <div className="flex-1">
                  <p className="text-xs font-semibold text-text-primary">
                    {a.url ? <a href={a.url} target="_blank" rel="noopener" className="hover:underline">{a.title}</a> : a.title}
                  </p>
                  <span className="text-[9px] text-text-muted">{a.source}</span>
                  {a.ai_reason && <p className="text-[10px] text-purple-light mt-1 italic">AI: {a.ai_reason}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AI Provider Settings */}
      {tab === 'settings' && (
        <div className="card p-4">
          <h2 className="text-sm font-semibold text-text-primary mb-3">AI Provider Configuration</h2>
          <ProviderSettings />
        </div>
      )}
    </div>
  )
}

function ProviderSettings() {
  const [providers, setProviders] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [switching, setSwitching] = useState(false)

  const fetchProviders = async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/ai/providers')
      setProviders(data)
    } finally { setLoading(false) }
  }

  useState(() => { fetchProviders() })

  const switchProvider = async (id: string) => {
    setSwitching(true)
    try {
      await api.put(`/api/ai/provider/${id}`)
      await fetchProviders()
    } finally { setSwitching(false) }
  }

  if (loading || !providers) return <p className="text-xs text-text-muted">Loading providers...</p>

  return (
    <div className="space-y-2">
      <p className="text-[10px] text-text-muted mb-3">Select the AI provider for summarisation, Q&A, and analysis. All providers use an OpenAI-compatible API.</p>
      {(providers.providers || []).map((p: any) => (
        <button key={p.id} type="button" onClick={() => switchProvider(p.id)} disabled={switching}
          className={clsx('w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors',
            providers.active === p.id ? 'border-purple/30 bg-purple/5' : 'border-border hover:bg-surface-hover',
            !p.configured && 'opacity-40')}>
          <div className={clsx('w-3 h-3 rounded-full shrink-0', providers.active === p.id ? 'bg-purple' : 'bg-surface-hover')} />
          <div className="flex-1">
            <span className="text-xs font-semibold text-text-primary">{p.name}</span>
            <span className="text-[9px] text-text-muted ml-2">{p.model}</span>
          </div>
          <span className={clsx('text-[9px]', p.configured ? 'text-emerald' : 'text-text-muted')}>
            {p.configured ? 'API key set' : 'Not configured'}
          </span>
          {providers.active === p.id && <span className="badge-purple text-[8px]">ACTIVE</span>}
        </button>
      ))}
      <p className="text-[9px] text-text-muted mt-2">API keys are set via server environment variables. Contact admin to configure.</p>
    </div>
  )
}
